/**
 * Average expense memoized selectors
 * All data are computed from customers counts and revenues fetched data
 */

import { createSelector } from '@reduxjs/toolkit';

import {
  getSelectedPeriod,
  getSelectedTimeAggregate,
  selectIsTaxIncluded,
} from '../../Preferences/selectors';
import { selectColorMapping, selectCurrency, selectStores } from '../../Stores/selectors';
import {
  areCustomersCountsLoading,
  getCustomersCountsError,
  getSelectedCustomersCountsChart,
} from '../CustomersCounts/selectors';
import { createChartData } from '../helpers';
import {
  areRevenuesLoading,
  getRevenuesError,
  getSelectedRevenuesChart,
} from '../Revenues/selectors';

/**
 * If last attempt at fetching data resulted in an error, returns the error value
 *
 * @return {string}
 */
export const getAverageSpentsError = createSelector(
  [getCustomersCountsError, getRevenuesError],
  (customersCountsError, revenuesError) => customersCountsError || revenuesError,
);

/**
 * If data is being fetched
 *
 * @return {boolean}
 */
export const areAverageSpentsLoading = createSelector(
  [areCustomersCountsLoading, areRevenuesLoading],
  (customersCountsLoading, revenuesLoading) => customersCountsLoading || revenuesLoading,
);

/**
 * Compute average expense based on revenues and customers counts data
 *
 * @return {Object}
 */
export const getSelectedAverageSpentsChart = createSelector(
  [getSelectedCustomersCountsChart, getSelectedRevenuesChart],
  (customersCounts, revenues) => {
    if (!customersCounts || !revenues) {
      return null;
    }

    return Object.keys(customersCounts).reduce((averageSpents, storeId) => {
      const currentCustomersCounts = customersCounts[storeId];
      const selected = currentCustomersCounts.selected
        ? Object.keys(currentCustomersCounts.selected.values).reduce((selected, value) => {
            const customersCounts =
              currentCustomersCounts.selected.values[value].value.customersCount;

            return {
              ...selected,
              [value]: {
                value: {
                  tax_excluded: customersCounts
                    ? revenues[storeId].selected.values[value].value.tax_excluded / customersCounts
                    : 0,
                  tax_included: customersCounts
                    ? revenues[storeId].selected.values[value].value.tax_included / customersCounts
                    : 0,
                },
              },
            };
          }, {})
        : null;
      const reference = currentCustomersCounts.reference
        ? Object.keys(currentCustomersCounts.reference.values).reduce((reference, value) => {
            const { customersCount } = currentCustomersCounts.reference.values[value].value;

            return {
              ...reference,
              [value]: {
                value: {
                  tax_excluded: customersCount
                    ? revenues[storeId].reference.values[value].value.tax_excluded / customersCount
                    : 0,
                  tax_included: customersCount
                    ? revenues[storeId].reference.values[value].value.tax_included / customersCount
                    : 0,
                },
              },
            };
          }, {})
        : null;

      return {
        ...averageSpents,
        [storeId]: {
          ...(selected ? { selected: { values: selected } } : {}),
          ...(reference ? { reference: { values: reference } } : {}),
        },
      };
    }, {});
  },
);

/**
 * Get average expense data chart without tax
 * Calculates average spent without tax for bar chart on selected stores
 *
 * @return {Array}
 */
export const getAverageSpentsChartTaxExcluded = createSelector(
  [
    getSelectedPeriod,
    getSelectedTimeAggregate,
    getSelectedAverageSpentsChart,
    selectCurrency,
    selectStores,
    selectColorMapping,
    getSelectedCustomersCountsChart,
  ],
  (period, aggregate, averageSpents, currency, stores, colorMapping, customersCounts) =>
    averageSpents && customersCounts
      ? createChartData({
          data: averageSpents,
          interval: period.interval,
          kpi: 'average-spent',
          tax: 'tax_excluded',
          aggregate,
          colorMapping,
          currency,
          customersCounts,
          stores,
        })
      : [],
);

/**
 * Get average expense data chart with tax
 * Calculates average expense with tax for bar chart on selected stores
 *
 * @return {Array}
 */
export const getAverageSpentsChartTaxIncluded = createSelector(
  [
    getSelectedPeriod,
    getSelectedTimeAggregate,
    getSelectedAverageSpentsChart,
    selectCurrency,
    selectStores,
    selectColorMapping,
    getSelectedCustomersCountsChart,
  ],
  (period, aggregate, averageSpents, currency, stores, colorMapping, customersCounts) =>
    averageSpents && customersCounts
      ? createChartData({
          data: averageSpents,
          interval: period.interval,
          tax: 'tax_included',
          aggregate,
          colorMapping,
          currency,
          customersCounts,
          stores,
        })
      : [],
);

/**
 * Calculates average expense for bar chart on selected stores
 *
 * @return {Array}
 */
export const getAverageSpentsChartData = createSelector(
  [selectIsTaxIncluded, getAverageSpentsChartTaxIncluded, getAverageSpentsChartTaxExcluded],
  (isTaxIncluded, averageSpentsTaxIncluded, averageSpentsTaxExcluded) =>
    isTaxIncluded ? averageSpentsTaxIncluded : averageSpentsTaxExcluded,
);
